import { emailSchema } from '@fluxus/shared';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { AuthScreen } from '@/components/layout/AuthScreen';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, PasswordInput, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { onOrphanSsoReturn } from '@/features/auth/sso-apps';
import { SsoStartForm } from '@/features/auth/SsoStartForm';
import { TwoFactorSetupStep } from '@/features/auth/TwoFactorSetupStep';
import { TwoFactorStep } from '@/features/auth/TwoFactorStep';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { homePathFor, safeReturnPath } from '@/lib/auth/access';
import { useAuth } from '@/lib/auth/session';
import { SessionLoadingPage } from './AccessPages';

/** Mesmas regras da API (loginRequestSchema), com mensagens para o formulário. */
const loginFormSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha.').max(128, 'A senha pode ter no máximo 128 caracteres.'),
});

type FieldErrors = Partial<Record<'email' | 'password', string>>;

export function LoginPage() {
  const { state, login, client } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Senha certa com 2FA ativo: o desafio da segunda etapa. */
  const [challenge, setChallenge] = useState<{ token: string; step: 'verify' | 'setup' } | null>(null);
  /** Entrar com SSO (T-028d): pelo e-mail da empresa, no provedor dela. */
  const [ssoMode, setSsoMode] = useState(false);
  /** Voltou do "Entrar com SSO": o foco vai para o e-mail (o botão que tinha o foco saiu da tela). */
  const [focusEmail, setFocusEmail] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const ids = { email: useId(), password: useId(), alert: useId() };
  // Login único na web, no desktop (retorno local) e no Android (deep link); o iOS fica de fora (DEC-006).
  const ssoAvailable = client.platform !== 'ios';
  // Android: volta do SSO que chegou sem uma tela esperando (o sistema encerrou o app no meio).
  useEffect(() => {
    if (client.platform !== 'android') return;
    let stop: (() => void) | undefined;
    let active = true;
    void onOrphanSsoReturn(() => {
      setSsoMode(false);
      setFormError(
        new Error(
          'A entrada pelo SSO não foi concluída porque o app foi fechado no meio dela. Toque em "Entrar com SSO" e comece de novo.',
        ),
      );
    }).then((s) => {
      if (active) stop = s;
      else s();
    });
    return () => {
      active = false;
      stop?.();
    };
  }, [client.platform]);
  useEffect(() => {
    if (!focusEmail || ssoMode) return;
    emailRef.current?.focus();
    setFocusEmail(false);
  }, [focusEmail, ssoMode]);

  // Enquanto a sessão é restaurada, não mostra o formulário (evita piscar antes de redirecionar).
  if (state.status === 'loading') return <SessionLoadingPage />;
  if (state.status === 'authenticated') {
    return <Navigate to={safeReturnPath(params.get('voltar')) ?? homePathFor(state.session.user)} replace />;
  }

  function restart(reason?: unknown) {
    setChallenge(null);
    setFormError(reason ?? null);
  }

  if (challenge) {
    return (
      <AuthScreen>
        {challenge.step === 'setup' ? (
          <TwoFactorSetupStep challengeToken={challenge.token} email={email} onRestart={restart} />
        ) : (
          <TwoFactorStep challengeToken={challenge.token} onRestart={restart} />
        )}
      </AuthScreen>
    );
  }

  if (ssoMode) {
    return (
      <AuthScreen>
        <SsoStartForm
          initialEmail={email}
          onBack={(typed) => {
            setEmail(typed);
            setSsoMode(false);
            setFocusEmail(true);
          }}
          onTwoFactor={(c) => {
            setEmail(c.email);
            setChallenge({ token: c.token, step: c.step });
          }}
        />
      </AuthScreen>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);
    const parsed = loginFormSchema.safeParse({ email, password });
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof FieldErrors;
        errors[field] ??= issue.message;
      }
      setFieldErrors(errors);
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      // Ao entrar, a sessão muda e esta tela redireciona para o destino do perfil.
      const outcome = await login(parsed.data.email, parsed.data.password);
      if (outcome.kind === 'two-factor') {
        setPassword('');
        setChallenge({ token: outcome.challengeToken, step: outcome.step });
      }
    } catch (err) {
      setFormError(err);
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  const locked = formError instanceof ApiError && formError.code === 'ACCOUNT_LOCKED';

  return (
    <AuthScreen>
      <h1 className="text-3xl font-extrabold tracking-tight">Entrar</h1>
      <p className="mt-2 text-sm text-muted-foreground">Use o e-mail e a senha da sua conta Fluxus BSC.</p>

      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-8 flex flex-col gap-5"
        aria-describedby={formError ? ids.alert : undefined}
      >
        <FormField id={ids.email} label="E-mail" error={fieldErrors.email}>
          <Input
            ref={emailRef}
            {...fieldA11y(ids.email, fieldErrors.email)}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldErrors((f) => ({ ...f, email: undefined }));
            }}
            className="h-11"
          />
        </FormField>

        <FormField
          id={ids.password}
          label="Senha"
          error={fieldErrors.password}
          action={
            // O e-mail digitado vai junto pelo estado da navegação (nunca no endereço).
            <Link
              to="/esqueci-senha"
              state={{ email }}
              className="text-sm font-semibold text-brand-strong underline-offset-4 hover:underline dark:text-brand"
            >
              Esqueci minha senha
            </Link>
          }
        >
          <PasswordInput
            ref={passwordRef}
            {...fieldA11y(ids.password, fieldErrors.password)}
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((f) => ({ ...f, password: undefined }));
            }}
          />
        </FormField>

        {formError ? (
          <FormAlert
            id={ids.alert}
            tone={locked ? 'locked' : 'error'}
            title={locked ? 'Acesso bloqueado temporariamente' : undefined}
          >
            {errorMessage(formError)}
          </FormAlert>
        ) : null}

        <Button type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
          Entrar
        </Button>
      </form>

      {ssoAvailable ? (
        <>
          <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground" aria-hidden>
            <span className="h-px flex-1 bg-border" />
            ou
            <span className="h-px flex-1 bg-border" />
          </div>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="mt-6 w-full"
            disabled={submitting}
            onClick={() => {
              setFormError(null);
              setFieldErrors({});
              setPassword('');
              setSsoMode(true);
            }}
          >
            Entrar com SSO
          </Button>
        </>
      ) : null}
    </AuthScreen>
  );
}
